import type { ReactNode } from 'react';
import { cx } from '../lib/cx';

/**
 * The one place a screen's width and padding are decided.
 *
 * Every page used to pick its own: 720, 760, 880, 960, 1080, 1100. All
 * centred, all different, so moving between Home, Review and Backlog made the
 * content jump sideways for no reason a reader could infer. None of those
 * numbers encoded anything — they were each chosen once, in isolation, and
 * never compared.
 *
 * Three widths, and a screen has to justify anything but the first:
 *
 *   content   the default. Vertical lists and reading columns: Home, Review,
 *             Backlog, Sprints, Completed, Projects, Settings, Account.
 *   wide      genuinely two-dimensional data that a narrow column would force
 *             into horizontal scrolling: Month end's tables, Planning's grid.
 *   full      surfaces that own the whole viewport and scroll themselves —
 *             the Board's columns.
 *
 * Width is capped rather than fluid on purpose. A reading column spanning a
 * 2560px monitor is harder to read, not easier, so `content` grows by about a
 * third on a large display and then stops.
 */

export type PageWidth = 'content' | 'wide' | 'full';

const WIDTH_CLASS: Readonly<Record<PageWidth, string>> = {
  content: 'mx-auto w-full max-w-[65rem] 2xl:max-w-[80rem]',
  wide: 'mx-auto w-full max-w-[80rem] 2xl:max-w-[97.5rem]',
  full: 'w-full',
};

export interface PageContainerProps {
  readonly children: ReactNode;
  readonly width?: PageWidth;
  /**
   * For screens that own their own scrolling and must fill the shell exactly —
   * the Board. Everything else scrolls as a page.
   */
  readonly fillHeight?: boolean;
  readonly className?: string;
}

export function PageContainer({
  children,
  width = 'content',
  fillHeight = false,
  className,
}: PageContainerProps) {
  return (
    <div
      className={cx(
        'animate-enter px-5 pt-9 pb-14 md:px-8',
        WIDTH_CLASS[width],
        fillHeight && 'h-full overflow-auto',
        className,
      )}
    >
      {children}
    </div>
  );
}
